import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef } from "react";
import useSWR from "swr";
import { mergeLiveMapFeed } from "./liveMapFeed.js";
const emptyVehicleRecords = {};
const vehicleMapDataEndpoint = "/api/vehiclepositions/mapdata";
export const liveVehicleMapCursorOverlapSeconds = 1;
export const liveVehicleMapSnapshotReconcileMs = 5 * 60 * 1000;
function toPositiveNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}
export function getLiveVehicleMapSince(currentData, {
  now = Date.now(),
  staleAfterMs,
  snapshotReconcileMs = liveVehicleMapSnapshotReconcileMs
} = {}) {
  const updatedAt = toPositiveNumber(currentData?.updatedAt);
  const receivedAt = toPositiveNumber(currentData?.receivedAt);
  const lastSnapshotAt = toPositiveNumber(currentData?.lastSnapshotAt);
  if (!updatedAt || !receivedAt || !lastSnapshotAt) {
    return null;
  }
  if (now - receivedAt > staleAfterMs || now - lastSnapshotAt > snapshotReconcileMs) {
    return null;
  }

  // Feed timestamps are second-resolution. Overlap the cursor so updates
  // sharing its timestamp cannot be skipped; mergeLiveMapFeed is idempotent.
  return Math.max(0, updatedAt - liveVehicleMapCursorOverlapSeconds);
}
export function mergeLiveVehicleMapData(currentData, payload, {
  defaultStaleAfterMs,
  now = Date.now()
} = {}) {
  const generatedAt = toPositiveNumber(payload?.generatedAt) || now;
  const currentUpdatedAt = toPositiveNumber(currentData?.updatedAt);
  const payloadUpdatedAt = toPositiveNumber(payload?.updatedAt);
  const isSnapshot = Boolean(payload?.snapshot);
  return {
    availability: payload?.availability ?? currentData?.availability ?? null,
    lastSnapshotAt: isSnapshot ? generatedAt : toPositiveNumber(currentData?.lastSnapshotAt) || generatedAt,
    receivedAt: generatedAt,
    updatedAt: Math.max(currentUpdatedAt, payloadUpdatedAt),
    vehicleRecords: mergeLiveMapFeed(currentData?.vehicleRecords || emptyVehicleRecords, payload, {
      defaultStaleAfterMs
    })
  };
}
function getLiveVehicleMapRequestUrl(url, since) {
  if (since == null) return url;
  return `${url}?since=${encodeURIComponent(since)}`;
}

/**
 * Fetch and normalize the live vehicle feed used by regional main maps.
 *
 * SWR owns request timing. The hook asks for full snapshots at startup, after
 * a stale gap, and periodically for reconciliation; otherwise it folds the
 * API's `since` response into the cached map state.
 *
 * Regional maps own conversion to map features because their badge/image
 * properties and provider-specific vehicle fields differ.
 */
export function useLiveVehicleMapData(t0) {
  const $ = _c(16);
  const {
    enabled,
    refreshInterval,
    staleAfterMs
  } = t0;
  const latestDataRef = useRef(null);
  let t1;
  if ($[0] !== staleAfterMs) {
    t1 = async url => {
      const now = Date.now();
      const currentData = latestDataRef.current;
      const since = getLiveVehicleMapSince(currentData, {
        now,
        staleAfterMs
      });
      const response = await fetch(getLiveVehicleMapRequestUrl(url, since), {
        cache: "no-store"
      });
      if (!response.ok) {
        throw new Error("Failed to load vehicle map data");
      }
      const payload = await response.json();
      return mergeLiveVehicleMapData(currentData, payload, {
        defaultStaleAfterMs: staleAfterMs,
        now
      });
    };
    $[0] = staleAfterMs;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fetchLiveVehicleMapData = t1;
  let t2;
  if ($[2] !== refreshInterval) {
    t2 = {
      refreshInterval,
      revalidateOnFocus: true,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      dedupingInterval: 10000
    };
    $[2] = refreshInterval;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data,
    error: requestError,
    isLoading,
    mutate: refresh
  } = useSWR(enabled ? vehicleMapDataEndpoint : null, fetchLiveVehicleMapData, t2);
  let t3;
  let t4;
  if ($[4] !== data) {
    t3 = () => {
      latestDataRef.current = data || null;
    };
    t4 = [data];
    $[4] = data;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== data?.availability || $[8] !== requestError) {
    t5 = requestError || (data?.availability === "degraded" ? new Error("Live vehicle data is degraded") : null);
    $[7] = data?.availability;
    $[8] = requestError;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const error = t5;
  const t6 = Boolean(data);
  const t7 = data?.vehicleRecords || emptyVehicleRecords;
  let t8;
  if ($[10] !== error || $[11] !== isLoading || $[12] !== refresh || $[13] !== t6 || $[14] !== t7) {
    t8 = {
      error,
      hasVehicleMapData: t6,
      isLoading,
      refresh,
      vehicleRecords: t7
    };
    $[10] = error;
    $[11] = isLoading;
    $[12] = refresh;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}